import Renderer from './Renderer';
import type { Coordinates, Dimensions } from '../../types/general.types';
import { ColorValue } from '../../helpers/color/color.types';
import { NailsRenderOptions } from '../../types/stringart.types';
import { areDimensionsEqual } from '../../helpers/size_utils';

const SVG_NS = 'http://www.w3.org/2000/svg';

export default class SVGRenderer extends Renderer {
  svg: SVGElement;
  backgroundGroup: SVGGElement;
  background: SVGRectElement = null;
  linesGroup: SVGGElement;
  nailsGroup: SVGGElement;
  nailsCirclesGroup: SVGGElement;
  nailsTextGroup: SVGGElement;
  currentColor: ColorValue | null = null;
  lineWidth: number | string = 1;
  currentLineGroup: SVGGElement = null;

  constructor(parentElement: HTMLElement) {
    super(parentElement);

    this.svg = document.createElementNS(SVG_NS, 'svg');
    this.svg.style.setProperty('display', 'block');
    this.backgroundGroup = document.createElementNS(SVG_NS, 'g');
    this.backgroundGroup.setAttribute('data-id', 'background');
    this.linesGroup = document.createElementNS(SVG_NS, 'g');
    this.linesGroup.setAttribute('data-id', 'lines');
    this.nailsGroup = document.createElementNS(SVG_NS, 'g');
    this.nailsGroup.setAttribute('data-id', 'nails');

    this.nailsCirclesGroup = document.createElementNS(SVG_NS, 'g');
    this.nailsCirclesGroup.setAttribute('data-id', 'nailsCircles');

    this.nailsTextGroup = document.createElementNS(SVG_NS, 'g');
    this.nailsTextGroup.setAttribute('data-id', 'nailsText');

    this.nailsGroup.appendChild(this.nailsCirclesGroup);
    this.nailsGroup.appendChild(this.nailsTextGroup);

    this.svg.appendChild(this.backgroundGroup);
    this.svg.appendChild(this.linesGroup);
    this.svg.appendChild(this.nailsGroup);

    this.svg.setAttribute('xmlns', SVG_NS);

    parentElement.appendChild(this.svg);
  }

  get element(): SVGElement {
    return this.svg;
  }

  resetSize(): Dimensions {
    super.resetSize();

    this.svg.style.removeProperty('width');
    this.svg.style.removeProperty('height');

    const newDimensions = this.getSize().map(Math.trunc) as Dimensions;
    const [width, height] = newDimensions;
    this.svg.setAttributeNS(SVG_NS, 'viewBox', `0 0 ${width} ${height}`);
    this.svg.setAttributeNS(SVG_NS, 'width', String(width));
    this.svg.setAttributeNS(SVG_NS, 'height', String(height));
    this.svg.style.width = width + 'px';
    this.svg.style.height = height + 'px';

    if (
      !this.currentSize ||
      !areDimensionsEqual(newDimensions, this.currentSize)
    ) {
      this.currentSize = newDimensions;
      this.emit('sizeChange', { size: newDimensions });
    }

    return newDimensions;
  }

  resetNails() {
    this.nailsCirclesGroup.innerHTML = '';
    this.nailsTextGroup.innerHTML = '';
  }

  resetStrings() {
    this.linesGroup.innerHTML = '';
    this.currentColor = null;
    this.lineWidth = null;
  }

  setColor(color: ColorValue) {
    if (color !== this.currentColor) {
      this.currentLineGroup = document.createElementNS(SVG_NS, 'g');
      this.currentLineGroup.setAttribute('stroke', color);
      this.currentLineGroup.setAttribute(
        'stroke-width',
        String(this.lineWidth)
      );
      this.linesGroup.appendChild(this.currentLineGroup);
      this.currentColor = color;
    }
  }

  setLineWidth(width?: number) {
    this.lineWidth = width ?? '1';
    this.linesGroup.setAttribute('stroke-width', String(width ?? 1));
    this.linesGroup.childNodes.forEach(group => {
      if (group instanceof SVGGElement) {
        group.setAttribute('stroke-width', String(width ?? 1));
      }
    });
  }

  setBackground(color: ColorValue) {
    if (color) {
      if (!this.background) {
        this.background = document.createElementNS(SVG_NS, 'rect');
        this.background.setAttribute('width', '100%');
        this.background.setAttribute('height', '100%');
        this.backgroundGroup.appendChild(this.background);
      }

      this.background.setAttribute('fill', color);
    } else {
      this.background = null;
      this.backgroundGroup.innerHTML = '';
    }
  }

  getSize(): Dimensions {
    return [this.svg.clientWidth, this.svg.clientHeight];
  }

  setSize(size: Dimensions | null) {
    if (!size) {
      return this.resetSize();
    }

    if (this.fixedSize) {
      console.warn(
        `Trying to set size for Renderer to [${size.join(
          ', '
        )}], but size is fixed to [${this.fixedSize.join(', ')}].`
      );
      return this.fixedSize;
    }

    this.svg.removeAttributeNS(SVG_NS, 'width');
    this.svg.removeAttributeNS(SVG_NS, 'height');

    this.svg.style.width = `${size[0]}px`;
    this.svg.style.height = `${size[1]}px`;

    const realSize = size.map(Math.trunc) as Dimensions;
    const [width, height] = realSize;

    this.svg.setAttributeNS(SVG_NS, 'viewBox', `0 0 ${width} ${height}`);
    this.svg.setAttributeNS(SVG_NS, 'width', String(width));
    this.svg.setAttributeNS(SVG_NS, 'height', String(height));

    if (!this.currentSize || !areDimensionsEqual(realSize, this.currentSize)) {
      this.currentSize = realSize;
      this.emit('sizeChange', { size: realSize });
    }

    return realSize;
  }

  renderLine(from: Coordinates, to: Coordinates): void {
    this.lastStringCoordinates = from;
    this.lineTo(to);
  }

  lineTo(to: Coordinates) {
    if (!this.lastStringCoordinates) {
      throw new Error("no last string coordinates, can't lineTo");
    }

    const line = document.createElementNS(SVG_NS, 'line');
    line.setAttribute('x1', String(Math.trunc(this.lastStringCoordinates[0])));
    line.setAttribute('y1', String(Math.trunc(this.lastStringCoordinates[1])));
    line.setAttribute('x2', String(Math.trunc(to[0])));
    line.setAttribute('y2', String(Math.trunc(to[1])));

    this.currentLineGroup.appendChild(line);

    this.lastStringCoordinates = to;
  }

  clearInstructions(): void {
    // TODO: Implement this
  }

  renderInstructions(from: Coordinates, to: Coordinates): void {
    // TODO: Implement this
  }

  renderInstructionsForLastLine(): void {
    // TODO: Implement this
  }

  hideInstructions(): void {
    // TODO: Implement this
  }

  showInstructions(): void {
    // TODO: Implement this
  }

  renderNails(
    nails: Iterable<Coordinates>,
    {
      color,
      fontSize,
      radius,
      renderNumbers,
      margin = 0,
      numbersStart = 1,
    }: NailsRenderOptions
  ) {
    const centerX = this.getSize()[0] / 2;
    //this.nailsCirclesGroup.innerHTML = this.nailsTextGroup.innerHTML = '';
    const circlesFragment = document.createDocumentFragment();
    const textFragment = document.createDocumentFragment();

    const nailNumberOffset = radius + margin;

    this.nailsTextGroup.style.fontSize = String(fontSize);
    let number = numbersStart;

    for (let [x, y] of nails) {
      const circle = document.createElementNS(SVG_NS, 'circle');
      circle.setAttribute('cx', String(x));
      circle.setAttribute('cy', String(y));
      circle.setAttribute('r', String(radius));
      circlesFragment.appendChild(circle);

      if (renderNumbers && number != null) {
        const isRightAlign = x < centerX;

        const numberPosition = [
          isRightAlign ? x - nailNumberOffset : x + nailNumberOffset,
          y,
        ];

        const textEl = document.createElementNS(SVG_NS, 'text');
        textEl.innerHTML = String(number);
        textEl.setAttribute('x', String(numberPosition[0]));
        textEl.setAttribute('y', String(numberPosition[1]));
        if (isRightAlign) {
          textEl.setAttribute('text-anchor', 'end');
        }
        textFragment.appendChild(textEl);
      }
      number++;
    }

    this.nailsCirclesGroup.appendChild(circlesFragment);
    this.nailsTextGroup.appendChild(textFragment);

    if (color != null) {
      this.nailsCirclesGroup.setAttribute('fill', color);
      this.nailsTextGroup.setAttribute('fill', color);
    }
  }

  clear() {
    this.linesGroup.innerHTML = '';
    this.nailsGroup.innerHTML = '';
  }

  toDataURL() {
    return '';
  }
}
